import { useEffect, useMemo, useState } from "react";
import { fr } from "date-fns/locale";
import { CheckCircle2, CalendarDays, Clock, Loader2, MapPin, Video, MessageCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "@/components/Reveal";
import { PaymentChoice } from "@/components/PaymentChoice";
import { GiftCodeField, remiseCadeau } from "@/components/GiftCodeField";
import { api, formatCHF } from "@/lib/api";

const inputCls = "w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra";
const chipCls = (active, disabled) =>
  `px-2 py-3 rounded-xl border text-xs font-mono transition-colors duration-300 ${
    disabled ? "border-wood/10 text-fumee/40 line-through cursor-not-allowed" : active ? "border-terra bg-terra/10 text-terra" : "border-wood/25 text-fumee hover:border-wood/50"
  }`;

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const longDate = (d) => new Intl.DateTimeFormat("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);
const hLabel = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return `${h} h${m ? ` ${String(m).padStart(2, "0")}` : ""}`; };
const ICONS = { question: MessageCircle, presentiel: MapPin, distance: Video };

const DEFAULT_CFG = { creneaux: ["09:00", "10:30", "14:00", "15:30", "17:00", "18:30"], ecart_min: 30, delai_jours: 2,
  formats: {
    question: { label: "Question ciblée", detail: "à distance uniquement", prix: 20, duree_min: 15 },
    distance: { label: "Consultation holistique", detail: "à distance — visio, téléphone ou WhatsApp", prix: 50, duree_min: 45 },
    presentiel: { label: "Consultation holistique", detail: "en présentiel à l'atelier d'Orbe", prix: 100, duree_min: 60 },
  } };

const creneauLibre = (jour, heure, dureeMin, occupations, cfg) => {
  const debut = new Date(`${jour}T${heure}:00`);
  const fin = new Date(debut.getTime() + dureeMin * 60e3);
  const ecart = cfg.ecart_min * 60e3;
  return (occupations[jour] || []).every(([od, of]) => !(debut < new Date(new Date(of).getTime() + ecart) && new Date(fin.getTime() + ecart) > new Date(od)));
};

export const ConsultationBooking = ({ modeInitial }) => {
  const [dispo, setDispo] = useState({ occupations: {}, cfg: DEFAULT_CFG });
  const [selected, setSelected] = useState(undefined);
  const [heure, setHeure] = useState(null);
  const [form, setForm] = useState({ mode_consultation: modeInitial || "distance", nom: "", email: "", telephone: "", paiement: "en_ligne", message: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [carte, setCarte] = useState(null);

  const refresh = () => api.disponibilites().then((d) => setDispo({ occupations: d.occupations || {}, cfg: d.consultation || DEFAULT_CFG, jours_fermes: (d.jours_fermes || [6]).map((j) => (j + 1) % 7), fermetures: d.fermetures || [], agenda_verifie: !!d.agenda_verifie })).catch(() => {});
  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (modeInitial) setForm((f) => ({ ...f, mode_consultation: modeInitial })); }, [modeInitial]);

  const cfg = dispo.cfg;
  const fmt = cfg.formats[form.mode_consultation] || cfg.formats.distance;
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + cfg.delai_jours);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 120);

  const jour = selected ? toKey(selected) : null;
  const creneaux = useMemo(() => cfg.creneaux.map((h) => ({ h, libre: jour ? creneauLibre(jour, h, fmt.duree_min, dispo.occupations, cfg) : true })), [jour, dispo, cfg, fmt]);
  const disabled = useMemo(() => [{ dayOfWeek: dispo.jours_fermes || [0] }, ...(dispo.fermetures || []).map((f) => ({ from: new Date(`${f.du}T00:00:00`), to: new Date(`${f.au}T23:59:59`) }))], [dispo]);

  useEffect(() => { setHeure(null); }, [jour, form.mode_consultation]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));
  const remise = remiseCadeau(carte, fmt.prix);
  const total = fmt.prix - remise;

  const submit = async (e) => {
    e.preventDefault();
    if (!selected || !heure) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.book({ type: "consultation", date_individuelle: jour, heure, ...form, participants: 1, origin_url: window.location.origin, code_cadeau: carte?.code || null });
      if (res.checkout_url) { window.location.assign(res.checkout_url); return; }
      setConfirmation(res);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue, merci de réessayer.");
      refresh();
    } finally {
      setLoading(false);
    }
  };

  if (confirmation) {
    return (
      <Reveal>
        <div className="bg-sage-light/50 border border-sage/30 rounded-2xl p-10 text-center" data-testid="consultation-success">
          <CheckCircle2 className="w-12 h-12 text-sage mx-auto mb-4" />
          <p className="font-serif text-3xl text-bark mb-3">Votre consultation est réservée</p>
          <p className="text-fumee max-w-md mx-auto">
            Référence <span className="font-mono text-terra">{confirmation.reference}</span> —{" "}
            <span className="capitalize">{longDate(new Date(confirmation.event_date))}</span>, {confirmation.horaire} ·{" "}
            {formatCHF(confirmation.montant)} · {confirmation.paiement === "carte_cadeau" ? "réglé par carte cadeau" : "règlement par facture sous 10 jours"}.
            Un courriel de confirmation arrive vers {confirmation.email}.
          </p>
          <p className="text-sm text-terra mt-4 font-mono tracking-wide" data-testid="consultation-confirmation-format">{confirmation.mode_label}</p>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal>
      <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="consultation-booking">
        <div className="bg-card border border-sand-dark rounded-2xl p-6 lg:p-8">
          <div className="mb-6">
            <span className="eyebrow block mb-3">Format</span>
            <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Format de la consultation">
              {Object.entries(cfg.formats).map(([k, f]) => {
                const Icon = ICONS[k] || MapPin;
                const active = form.mode_consultation === k;
                return (
                  <button type="button" key={k} data-testid={`consultation-mode-${k}`} onClick={() => setForm((prev) => ({ ...prev, mode_consultation: k }))}
                    className={`flex items-start gap-3 text-left rounded-xl border px-3 py-3 transition-colors duration-300 ${active ? "border-terra bg-terra/5" : "border-wood/20 hover:border-wood/40"}`}>
                    <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${active ? "text-terra" : "text-fumee"}`} />
                    <span className="min-w-0">
                      <span className={`block text-sm font-medium leading-snug ${active ? "text-terra" : "text-bark"}`}>{f.label} · {f.duree_min} min</span>
                      <span className="block text-[11px] text-fumee mt-0.5 leading-snug">{f.detail}</span>
                      <span className="block font-mono text-xs text-terra mt-1.5">{formatCHF(f.prix)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <p className="eyebrow mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Choisissez le jour</p>
          <div data-testid="consultation-calendar" className="flex justify-center">
            <Calendar mode="single" selected={selected} onSelect={setSelected} disabled={disabled} fromDate={minDate} toDate={maxDate} locale={fr} weekStartsOn={1} showOutsideDays={false} />
          </div>
          <div className="mt-5">
            <p className="eyebrow mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Heure de début</p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Créneau horaire">
              {creneaux.map(({ h, libre }) => (
                <button type="button" key={h} disabled={!selected || !libre} data-testid={`consultation-creneau-${h.replace(":", "")}`}
                  onClick={() => setHeure(h)} className={chipCls(heure === h, selected && !libre)}>
                  {hLabel(h)}
                </button>
              ))}
            </div>
            <p className="text-xs text-fumee mt-3 leading-relaxed">
              {fmt.label} : {fmt.duree_min} min. Jours fermés et créneaux déjà pris sont grisés{dispo.agenda_verifie ? ", agenda de Ludovic vérifié en direct" : ""}.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-card border border-sand-dark rounded-2xl p-6 lg:p-8 space-y-4" data-testid="consultation-form">
          <div className="flex items-baseline justify-between gap-4">
            <p className="font-serif text-2xl text-bark">Vous & votre question</p>
            <span className={`font-mono text-xs tracking-wider text-right ${selected && heure ? "text-terra" : "text-fumee"}`} data-testid="consultation-date-choisie">
              {selected ? `${longDate(selected)}${heure ? ` · ${hLabel(heure)}` : ""}` : "Aucune date choisie"}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cons-nom" className="eyebrow block mb-2">Nom complet</label>
              <input id="cons-nom" data-testid="consultation-nom" required value={form.nom} onChange={set("nom")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="cons-email" className="eyebrow block mb-2">Courriel</label>
              <input id="cons-email" data-testid="consultation-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
            </div>
          </div>
          <div>
            <label htmlFor="cons-tel" className="eyebrow block mb-2">Téléphone {form.mode_consultation !== "presentiel" ? "(WhatsApp / appel à l'heure du rendez-vous)" : ""}</label>
            <input id="cons-tel" data-testid="consultation-telephone" type="tel" required minLength={6} value={form.telephone} onChange={set("telephone")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="cons-msg" className="eyebrow block mb-2">Votre question / intention (optionnel)</label>
            <textarea id="cons-msg" data-testid="consultation-message" rows={3} value={form.message} onChange={set("message")}
              placeholder="Une situation à éclairer, un ressenti, un schéma qui se répète, un lien avec un proche disparu…" className={`${inputCls} resize-none`} />
          </div>
          <div>
            <span className="eyebrow block mb-2">Paiement</span>
            <PaymentChoice compact value={form.paiement} onChange={(v) => setForm((prev) => ({ ...prev, paiement: v }))} testidPrefix="consultation" />
          </div>
          <GiftCodeField carte={carte} onChange={setCarte} testidPrefix="consultation" />
          {error && <p data-testid="consultation-error" className="text-sm text-destructive">{error}</p>}
          <div className="flex items-center justify-between pt-2">
            <div>
              <span className="font-serif text-xl text-bark" data-testid="consultation-total">{formatCHF(total)}</span>
              {remise > 0 && <p className="text-[11px] text-terra mt-1" data-testid="consultation-remise">carte cadeau : − {formatCHF(remise)}</p>}
            </div>
            <button type="submit" data-testid="consultation-submit" disabled={loading || !selected || !heure}
              className="px-7 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-earth transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Un instant…" : form.paiement === "en_ligne" && total > 0 ? "Réserver et payer (TWINT / carte)" : "Réserver"}
            </button>
          </div>
        </form>
      </div>
    </Reveal>
  );
};
